import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { listCasinos } from "@/data/casinos";
import { paymentMethods } from "@/data/payments";
import { providers, games } from "@/data/providers";
import { guides } from "@/data/guides";
import {
  casinoReviewPath,
  guidePath,
  paymentPath,
  providerPath,
  slotPath,
} from "@/lib/format";

type Result = {
  href: string;
  label: string;
  hint: string;
  kind: "casino" | "payment" | "guide" | "slot" | "provider";
};

type Props = { onSelect?: () => void; autoFocus?: boolean };

export function SearchBar({ onSelect, autoFocus }: Props) {
  const { t, lang, prefix } = useI18n();
  const navigate = useNavigate();
  const isSv = lang === "sv";
  const [q, setQ] = useState("");
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  const results = useMemo<Result[]>(() => {
    if (!q.trim()) return [];
    const needle = q.trim().toLowerCase();
    const out: Result[] = [];
    listCasinos({ searchQuery: needle })
      .slice(0, 4)
      .forEach((c) =>
        out.push({
          href: casinoReviewPath(prefix, lang, c.slug),
          label: c.name,
          hint: isSv ? "Casino-recension" : "Casino review",
          kind: "casino",
        }),
      );
    paymentMethods
      .filter(
        (p) =>
          p.name.toLowerCase().includes(needle) ||
          p.slug_sv.includes(needle) ||
          p.slug_en.includes(needle),
      )
      .slice(0, 3)
      .forEach((p) =>
        out.push({
          href: paymentPath(prefix, lang, isSv ? p.slug_sv : p.slug_en),
          label: p.name,
          hint: isSv ? "Betalningsmetod" : "Payment method",
          kind: "payment",
        }),
      );
    games
      .filter(
        (g) =>
          g.name.toLowerCase().includes(needle) || g.slug.includes(needle),
      )
      .slice(0, 3)
      .forEach((g) =>
        out.push({
          href: slotPath(prefix, lang, g.slug),
          label: g.name,
          hint: g.provider_slug,
          kind: "slot",
        }),
      );
    providers
      .filter(
        (p) => p.name.toLowerCase().includes(needle) || p.slug.includes(needle),
      )
      .slice(0, 3)
      .forEach((p) =>
        out.push({
          href: providerPath(prefix, lang, p.slug),
          label: p.name,
          hint: isSv ? "Speltillverkare" : "Provider",
          kind: "provider",
        }),
      );
    guides
      .filter((g) => {
        const title = isSv ? g.title_sv : g.title_en;
        const excerpt = isSv ? g.excerpt_sv : g.excerpt_en;
        return (
          title.toLowerCase().includes(needle) ||
          excerpt.toLowerCase().includes(needle)
        );
      })
      .slice(0, 3)
      .forEach((g) =>
        out.push({
          href: guidePath(prefix, lang, isSv ? g.slug_sv : g.slug_en),
          label: isSv ? g.title_sv : g.title_en,
          hint: isSv ? "Guide" : "Guide",
          kind: "guide",
        }),
      );
    return out;
  }, [q, isSv, lang, prefix]);

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && results[highlight]) {
      e.preventDefault();
      navigate(results[highlight].href);
      setQ("");
      onSelect?.();
    }
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-2 rounded-xl border border-ink-200 bg-white px-3 py-2 focus-within:border-brand-500 focus-within:ring-4 focus-within:ring-brand-200/40">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-ink-400"
          aria-hidden
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
        <input
          ref={inputRef}
          type="search"
          value={q}
          placeholder={t("search.placeholder")}
          onChange={(e) => {
            setQ(e.target.value);
            setHighlight(0);
          }}
          onKeyDown={handleKey}
          className="w-full bg-transparent text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none"
          aria-label={t("nav.search")}
          autoComplete="off"
        />
        {q && (
          <button
            type="button"
            onClick={() => setQ("")}
            className="text-xs font-semibold text-ink-500 hover:text-ink-700"
          >
            {t("cta.clear")}
          </button>
        )}
      </div>
      {q && results.length > 0 && (
        <ul
          className="absolute left-0 right-0 top-full z-30 mt-2 max-h-[60vh] overflow-y-auto rounded-xl border border-ink-100 bg-white p-1 shadow-card-hover"
          role="listbox"
        >
          {results.map((r, i) => (
            <li key={r.href}>
              <Link
                to={r.href}
                onClick={() => {
                  setQ("");
                  onSelect?.();
                }}
                onMouseEnter={() => setHighlight(i)}
                className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm no-underline ${
                  i === highlight ? "bg-brand-50 text-brand-800" : "text-ink-800"
                }`}
                role="option"
                aria-selected={i === highlight}
              >
                <span className="flex items-center gap-2">
                  <span className="grid h-6 w-6 place-items-center rounded-md bg-brand-50 text-[11px] font-bold text-brand-800">
                    {iconForKind(r.kind)}
                  </span>
                  <span>{r.label}</span>
                </span>
                <span className="text-[11px] text-ink-500">{r.hint}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {q && results.length === 0 && (
        <p className="mt-2 rounded-xl bg-ink-50 px-3 py-2 text-sm text-ink-500">
          {isSv ? "Inga träffar." : "No matches."}
        </p>
      )}
    </div>
  );
}

function iconForKind(kind: Result["kind"]): string {
  switch (kind) {
    case "casino":
      return "★";
    case "payment":
      return "$";
    case "slot":
      return "▣";
    case "provider":
      return "◆";
    case "guide":
      return "?";
  }
}
